
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./SubirPedidos.css";

const SubirPedidos = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [subidasHoy, setSubidasHoy] = useState(null);
  const [actividadReciente, setActividadReciente] = useState(null);

  const fetchSubidasHoy = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/pedidos-upload/subidas-hoy/`);
      setSubidasHoy(res.data);
    } catch (err) {
      console.error("Error cargando subidas:", err);
    }
  }, []);

  const fetchActividad = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/pedidos-upload/actividad/`);
      setActividadReciente(res.data);
    } catch (err) {
      console.error("Error cargando actividad:", err);
    }
  }, []);

  useEffect(() => {
    fetchSubidasHoy();
    fetchActividad();
    const interval = setInterval(() => {
      fetchSubidasHoy();
      fetchActividad();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchSubidasHoy, fetchActividad]);

  const handleUpload = async () => {
    if (!file) {
      Swal.fire({
        title: "Archivo no seleccionado",
        text: "Por favor selecciona un archivo Excel.",
        icon: "warning",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pedidos-upload/upload_pedido/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;

      // Feedback enriquecido
      let htmlContent = `
        <div style="text-align: left; font-size: 14px;">
          <p><strong>📁 Archivo:</strong> ${data.filename}</p>
          <p><strong>📍 Sucursal detectada:</strong> ${data.sucursal_detectada}</p>
          <p><strong>🕐 Hora:</strong> ${data.hora}</p>
        </div>
      `;

      if (data.es_duplicado) {
        htmlContent += `
          <div style="background: #fff3cd; border: 1px solid #ffc107; border-radius: 8px; padding: 10px; margin-top: 10px; text-align: left;">
            <strong>⚠️ Nota:</strong> Ya existía un pedido de esta sucursal hoy. Se guardó como actualización.
          </div>
        `;
      }

      Swal.fire({
        title: "✅ Pedido subido correctamente",
        html: htmlContent,
        icon: "success",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#10b981",
      });

      setFile(null);
      // Refresh panels
      fetchSubidasHoy();
      fetchActividad();

    } catch (error) {
      console.error("Error al subir pedido:", error);
      Swal.fire({
        title: "Error",
        text: "Hubo un problema al subir el archivo.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="subir-pedidos-container">
      <h2>Subir Pedidos</h2>

      <label>
        Archivo Excel (.xlsx, .xls):
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={(e) => setFile(e.target.files[0])}
        />
      </label>
      {file && <p>Archivo seleccionado: <strong>{file.name}</strong></p>}

      <button onClick={handleUpload} disabled={uploading}>
        {uploading ? "Subiendo..." : "Subir Pedido"}
      </button>

      {/* ═══ Panel: Sucursales que han subido hoy ═══ */}
      {subidasHoy && (
        <div className="feedback-panel">
          <div className="feedback-header">
            <h3>📦 Sucursales que han subido pedidos hoy</h3>
            <span className="feedback-badge">{subidasHoy.total_sucursales} sucursales</span>
          </div>

          {subidasHoy.sucursales_unicas.length > 0 ? (
            <div className="sucursales-chips">
              {subidasHoy.sucursales_unicas.map((suc, i) => (
                <span key={i} className="sucursal-chip">{suc}</span>
              ))}
            </div>
          ) : (
            <p className="no-data">Aún no se han subido pedidos hoy.</p>
          )}

          {subidasHoy.detalle.length > 0 && (
            <div className="detalle-subidas">
              <h4>Detalle de subidas ({subidasHoy.total_subidas})</h4>
              <div className="detalle-lista">
                {subidasHoy.detalle.map((item, i) => (
                  <div key={i} className={`detalle-item ${item.es_duplicado ? 'duplicado' : ''}`}>
                    <span className="detalle-hora">{item.hora}</span>
                    <span className="detalle-suc">SUC {item.sucursal}</span>
                    <span className="detalle-file" title={item.filename}>{item.filename}</span>
                    {item.es_duplicado && <span className="detalle-tag-dup">Actualización</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ Panel: Actividad Reciente ═══ */}
      {actividadReciente && actividadReciente.actividad.length > 0 && (
        <div className="feedback-panel actividad-panel">
          <div className="feedback-header">
            <h3>🔔 Actividad Reciente</h3>
            <span className="feedback-badge">{actividadReciente.total_eventos} eventos</span>
          </div>
          <div className="actividad-lista">
            {actividadReciente.actividad.slice(0, 15).map((evento, i) => (
              <div key={i} className={`actividad-item ${evento.tipo}`}>
                <span className="actividad-icono">
                  {evento.tipo === "subida" ? "📤" : "🔍"}
                </span>
                <span className="actividad-hora">{evento.hora}</span>
                <span className="actividad-desc">
                  {evento.tipo === "subida"
                    ? `SUC ${evento.sucursal} subió pedido`
                    : `SUC ${evento.sucursal} fue comparada`
                  }
                </span>
                {evento.tipo === "comparacion" && evento.stats && (
                  <span className="actividad-stats">
                    ✅{evento.stats.completo} ⚠️{evento.stats.parcial + evento.stats.conStock}
                  </span>
                )}
                {evento.es_duplicado && <span className="detalle-tag-dup">↺</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubirPedidos;
